var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { faCalendarAlt, faChevronDown } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// Day of week options (Monday-Saturday, then Sunday at end)
var DAY_OF_WEEK_OPTIONS = [
    { value: 1, label: "Monday", short: "Mon" },
    { value: 2, label: "Tuesday", short: "Tue" },
    { value: 3, label: "Wednesday", short: "Wed" },
    { value: 4, label: "Thursday", short: "Thu" },
    { value: 5, label: "Friday", short: "Fri" },
    { value: 6, label: "Saturday", short: "Sat" },
    { value: 0, label: "Sunday", short: "Sun" },
];
/**
 * DateRangePicker - A comprehensive date range picker with presets and shift times
 * Supports quick presets (1D, 7D, etc.) and custom date selection with optional time filtering
 */
var DateRangePicker = React.memo(function (_a) {
    var startDate = _a.startDate, endDate = _a.endDate, onChange = _a.onChange, _b = _a.className, className = _b === void 0 ? "" : _b, _c = _a.disabled, disabled = _c === void 0 ? false : _c, _d = _a.defaultStartTime, defaultStartTime = _d === void 0 ? null : _d, _e = _a.defaultEndTime, defaultEndTime = _e === void 0 ? null : _e, _f = _a.preset, preset = _f === void 0 ? "custom" : _f, _g = _a.showDayOfWeekFilter, showDayOfWeekFilter = _g === void 0 ? true : _g, _h = _a.selectedDays, propSelectedDays = _h === void 0 ? [] : _h;
    var _j = useState(false), isOpen = _j[0], setIsOpen = _j[1];
    var _k = useState(""), tempStartDate = _k[0], setTempStartDate = _k[1];
    var _l = useState(""), tempEndDate = _l[0], setTempEndDate = _l[1];
    var _m = useState(preset !== "custom" ? preset : null), activePreset = _m[0], setActivePreset = _m[1];
    var _o = useState(function () { return propSelectedDays.length > 0; }), enableDayOfWeekFilter = _o[0], setEnableDayOfWeekFilter = _o[1];
    var _p = useState(propSelectedDays), selectedDays = _p[0], setSelectedDays = _p[1];
    var _q = useState(null), hoveredDay = _q[0], setHoveredDay = _q[1];
    var prevPropSelectedDaysRef = useRef("");
    // Dynamic year labels
    var currentYear = useMemo(function () { return new Date().getFullYear(); }, []);
    var lastYear = useMemo(function () { return currentYear - 1; }, [currentYear]);
    // Update selected days when prop changes (but don't auto-collapse the filter)
    useEffect(function () {
        // Create a stable string representation to compare
        var currentPropsKey = propSelectedDays.sort().join(",");
        // Only update if the actual content changed
        if (currentPropsKey !== prevPropSelectedDaysRef.current) {
            prevPropSelectedDaysRef.current = currentPropsKey;
            setSelectedDays(propSelectedDays);
            // Only auto-enable if we have days from props, but don't auto-disable
            if (propSelectedDays.length > 0) {
                setEnableDayOfWeekFilter(true);
            }
        }
    }, [propSelectedDays]);
    var _r = useState(function () {
        if (defaultStartTime) {
            // Convert 24-hour format to 12-hour format for display
            var _a = defaultStartTime.split(":"), hours = _a[0], minutes = _a[1];
            var hour = parseInt(hours || "0");
            var ampm = hour >= 12 ? "PM" : "AM";
            var hour12 = hour % 12 || 12;
            return "".concat(hour12, ":").concat(minutes || "00", " ").concat(ampm);
        }
        return "No time filter";
    }), shiftStartTime = _r[0], setShiftStartTime = _r[1];
    var _s = useState(function () {
        if (defaultEndTime) {
            // Convert 24-hour format to 12-hour format for display
            var _a = defaultEndTime.split(":"), hours = _a[0], minutes = _a[1];
            var hour = parseInt(hours || "0");
            var ampm = hour >= 12 ? "PM" : "AM";
            var hour12 = hour % 12 || 12;
            return "".concat(hour12, ":").concat(minutes || "00", " ").concat(ampm);
        }
        return "No time filter";
    }), shiftEndTime = _s[0], setShiftEndTime = _s[1];
    var _t = useState(function () {
        return !!(defaultStartTime || defaultEndTime);
    }), enableShiftTimes = _t[0], setEnableShiftTimes = _t[1];
    // Update shift times when defaultStartTime/defaultEndTime props change (for preset loading)
    useEffect(function () {
        if (defaultStartTime) {
            // Convert 24-hour format to 12-hour format for display
            var _a = defaultStartTime.split(":"), hours = _a[0], minutes = _a[1];
            var hour = parseInt(hours || "0");
            var ampm = hour >= 12 ? "PM" : "AM";
            var hour12 = hour % 12 || 12;
            setShiftStartTime("".concat(hour12, ":").concat(minutes || "00", " ").concat(ampm));
        }
        else {
            setShiftStartTime("No time filter");
        }
    }, [defaultStartTime]);
    useEffect(function () {
        if (defaultEndTime) {
            // Convert 24-hour format to 12-hour format for display
            var _a = defaultEndTime.split(":"), hours = _a[0], minutes = _a[1];
            var hour = parseInt(hours || "0");
            var ampm = hour >= 12 ? "PM" : "AM";
            var hour12 = hour % 12 || 12;
            setShiftEndTime("".concat(hour12, ":").concat(minutes || "00", " ").concat(ampm));
        }
        else {
            setShiftEndTime("No time filter");
        }
    }, [defaultEndTime]);
    useEffect(function () {
        setEnableShiftTimes(!!(defaultStartTime || defaultEndTime));
    }, [defaultStartTime, defaultEndTime]);
    // Update active preset when preset prop changes
    useEffect(function () {
        setActivePreset(preset !== "custom" ? preset : null);
    }, [preset]);
    // Convert ISO strings to date input format (YYYY-MM-DD)
    var formatDateForInput = function (isoString) {
        if (!isoString)
            return "";
        try {
            var date = new Date(isoString);
            var datePart = date.toISOString().split("T")[0];
            return datePart || "";
        }
        catch (_a) {
            return "";
        }
    };
    // Format date for display
    var formatDateForDisplay = function (isoString) {
        if (!isoString)
            return "";
        try {
            var date = new Date(isoString);
            return date.toLocaleDateString();
        }
        catch (_a) {
            return "";
        }
    };
    // Get display text for the collapsed button
    var getDisplayText = function () {
        if (!startDate || !endDate)
            return "Select Date Range";
        return "".concat(formatDateForDisplay(startDate), " - ").concat(formatDateForDisplay(endDate));
    };
    // Open the picker and initialize temp values
    var handleOpen = useCallback(function () {
        setTempStartDate(formatDateForInput(startDate));
        setTempEndDate(formatDateForInput(endDate));
        setActivePreset(preset !== "custom" ? preset : null);
        setIsOpen(true);
    }, [startDate, endDate, preset]);
    // Convert 12-hour time format to 24-hour format
    var convertTo24Hour = function (time12) {
        if (time12 === "No time filter")
            return null;
        var parts = time12.split(" ");
        if (parts.length !== 2)
            return null;
        var time = parts[0], modifier = parts[1];
        if (!time || !modifier)
            return null;
        var timeParts = time.split(":");
        if (timeParts.length !== 2)
            return null;
        var hours = timeParts[0], minutes = timeParts[1];
        if (!hours || !minutes)
            return null;
        var hourNum = parseInt(hours, 10);
        if (modifier === "AM") {
            if (hourNum === 12) {
                hourNum = 0; // 12:xx AM becomes 00:xx
            }
        }
        else if (modifier === "PM") {
            if (hourNum !== 12) {
                hourNum += 12; // 1:xx PM becomes 13:xx, but 12:xx PM stays 12:xx
            }
        }
        return "".concat(hourNum.toString().padStart(2, "0"), ":").concat(minutes);
    };
    // Close the picker and reset temp state
    var handleClose = useCallback(function () {
        setIsOpen(false);
        setActivePreset(preset !== "custom" ? preset : null);
        setTempStartDate(formatDateForInput(startDate));
        setTempEndDate(formatDateForInput(endDate));
    }, [preset, startDate, endDate]);
    // Create date preset
    var createDatePreset = function (preset) {
        var now = new Date();
        var endDate;
        var startDate;
        switch (preset) {
            case "1d":
                endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
                startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 0, 0, 0, 0);
                break;
            case "7d":
                endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
                startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7, 0, 0, 0, 0);
                break;
            case "14d":
                endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
                startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 14, 0, 0, 0, 0);
                break;
            case "30d":
                endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
                startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30, 0, 0, 0, 0);
                break;
            case "90d":
                endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
                startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 90, 0, 0, 0, 0);
                break;
            case "ytd":
                // Year to date - from Jan 1 of current year to today
                endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
                startDate = new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0);
                break;
            case "lastyear":
                // Full previous year - Jan 1 to Dec 31 of last year
                endDate = new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59, 999);
                startDate = new Date(now.getFullYear() - 1, 0, 1, 0, 0, 0, 0);
                break;
            default:
                endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
                startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30, 0, 0, 0, 0);
        }
        return {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            preset: preset,
        };
    };
    // Toggle day selection
    var toggleDaySelection = function (day) {
        setSelectedDays(function (prev) {
            var newDays = prev.includes(day) ? prev.filter(function (d) { return d !== day; }) : __spreadArray(__spreadArray([], prev, true), [day], false).sort(function (a, b) { return a - b; });
            return newDays;
        });
    };
    // Handle preset selection - update temp state without applying
    var handlePresetClick = useCallback(function (preset) {
        setActivePreset(preset);
        var dateRange = createDatePreset(preset);
        // Update temp dates to show the preset range in the date inputs
        setTempStartDate(formatDateForInput(dateRange.startDate));
        setTempEndDate(formatDateForInput(dateRange.endDate));
        // Don't call onChange or close dialog - wait for Apply button
    }, []);
    // Apply the changes
    var handleApply = useCallback(function () {
        var newStartDate = tempStartDate ? new Date(tempStartDate + "T00:00:00.000Z").toISOString() : "";
        var newEndDate = tempEndDate ? new Date(tempEndDate + "T23:59:59.999Z").toISOString() : "";
        // Convert shift times to 24-hour format for API
        var startTime24 = enableShiftTimes ? convertTo24Hour(shiftStartTime) : null;
        var endTime24 = enableShiftTimes ? convertTo24Hour(shiftEndTime) : null;
        // Include selected days if day of week filter is enabled
        var daysToInclude = enableDayOfWeekFilter && selectedDays.length > 0 ? selectedDays : undefined;
        // Use activePreset if one is selected, otherwise "custom"
        var presetToUse = activePreset || "custom";
        onChange({
            startDate: newStartDate,
            endDate: newEndDate,
            preset: presetToUse,
            startTime: startTime24,
            endTime: endTime24,
            selectedDays: daysToInclude,
        });
        setIsOpen(false);
    }, [
        tempStartDate,
        tempEndDate,
        enableShiftTimes,
        shiftStartTime,
        shiftEndTime,
        enableDayOfWeekFilter,
        selectedDays,
        activePreset,
        onChange,
    ]);
    // Shift time options
    var timeOptions = [
        "No time filter",
        "12:00 AM",
        "12:30 AM",
        "1:00 AM",
        "1:30 AM",
        "2:00 AM",
        "2:30 AM",
        "3:00 AM",
        "3:30 AM",
        "4:00 AM",
        "4:30 AM",
        "5:00 AM",
        "5:30 AM",
        "6:00 AM",
        "6:30 AM",
        "7:00 AM",
        "7:30 AM",
        "8:00 AM",
        "8:30 AM",
        "9:00 AM",
        "9:30 AM",
        "10:00 AM",
        "10:30 AM",
        "11:00 AM",
        "11:30 AM",
        "12:00 PM",
        "12:30 PM",
        "1:00 PM",
        "1:30 PM",
        "2:00 PM",
        "2:30 PM",
        "3:00 PM",
        "3:30 PM",
        "4:00 PM",
        "4:30 PM",
        "5:00 PM",
        "5:30 PM",
        "6:00 PM",
        "6:30 PM",
        "7:00 PM",
        "7:30 PM",
        "8:00 PM",
        "8:30 PM",
        "9:00 PM",
        "9:30 PM",
        "10:00 PM",
        "10:30 PM",
        "11:00 PM",
        "11:30 PM",
    ];
    return (React.createElement("div", { className: "date-range-picker ".concat(className, " position-relative d-inline-block") },
        React.createElement("button", { type: "button", onClick: handleOpen, disabled: disabled, className: "d-flex align-items-center justify-content-between", style: {
                gap: "8px",
                padding: "6px 12px",
                backgroundColor: "white",
                border: "1px solid var(--ps-gray4)",
                borderRadius: "var(--ps-border-radius)",
                fontSize: "14px",
                color: "var(--ps-gray2)",
                cursor: disabled ? "not-allowed" : "pointer",
                minWidth: "200px",
                width: "100%",
                boxShadow: isOpen ? "0 1px 3px rgba(0, 0, 0, 0.1)" : "0 1px 3px rgba(0, 0, 0, 0.1)",
                borderColor: isOpen ? "var(--ps-primary-blue)" : "var(--ps-gray4)",
                minHeight: "34px",
                transition: "all 0.2s ease",
            }, onMouseEnter: function (e) {
                if (!disabled && !isOpen) {
                    e.currentTarget.style.borderColor = "#a0a8b0";
                }
            }, onMouseLeave: function (e) {
                if (!disabled && !isOpen) {
                    e.currentTarget.style.borderColor = "#d0d7de";
                }
            } },
            React.createElement("div", { className: "d-flex align-items-center", style: { gap: "8px" } },
                React.createElement(FontAwesomeIcon, { icon: faCalendarAlt, className: "text-muted", style: { fontSize: "12px" } }),
                React.createElement("span", null, getDisplayText())),
            React.createElement(FontAwesomeIcon, { icon: faChevronDown, className: "text-muted", style: {
                    fontSize: "10px",
                    transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.2s ease",
                } })),
        isOpen && (React.createElement(React.Fragment, null,
            React.createElement("div", { style: {
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 999,
                }, onClick: handleClose }),
            React.createElement("div", { style: {
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    marginTop: "4px",
                    backgroundColor: "white",
                    border: "1px solid #d0d7de",
                    borderRadius: "4px",
                    padding: "16px",
                    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.2)",
                    zIndex: 1000,
                    minWidth: "525px",
                    maxWidth: "625px",
                }, onClick: function (e) { return e.stopPropagation(); } },
                React.createElement("div", { className: "mb-15" },
                    React.createElement("div", { className: "d-flex flex-wrap justify-content-start", style: {
                            gap: "8px",
                        } }, ["1d", "7d", "14d", "30d", "90d", "ytd", "lastyear"].map(function (preset) {
                        var getPresetDisplayText = function (p) {
                            if (p === "ytd")
                                return currentYear.toString();
                            if (p === "lastyear")
                                return lastYear.toString();
                            return p.toUpperCase();
                        };
                        return (React.createElement("button", { key: preset, type: "button", onClick: function () { return handlePresetClick(preset); }, style: {
                                padding: "8px 16px",
                                border: "1px solid #d0d7de",
                                borderRadius: "4px",
                                backgroundColor: activePreset === preset ? "#00aec7" : "#f8f9fa",
                                color: activePreset === preset ? "white" : "#24292f",
                                cursor: "pointer",
                                fontSize: "14px",
                                fontWeight: "600",
                                transition: "all 0.2s ease",
                            }, title: getPresetLabel(preset), onMouseOver: function (e) {
                                if (activePreset !== preset) {
                                    e.currentTarget.style.backgroundColor = "#e9ecef";
                                }
                            }, onMouseOut: function (e) {
                                if (activePreset !== preset) {
                                    e.currentTarget.style.backgroundColor = "#f8f9fa";
                                }
                            } }, getPresetDisplayText(preset)));
                    }))),
                React.createElement("div", { className: "mb-15" },
                    React.createElement("div", { className: "d-flex mb-10", style: { gap: "12px" } },
                        React.createElement("div", { className: "flex-1" },
                            React.createElement("label", { className: "d-block mb-5", style: {
                                    fontSize: "14px",
                                    fontWeight: "500",
                                } }, "Start Date"),
                            React.createElement("input", { type: "date", value: tempStartDate, onChange: function (e) {
                                    setTempStartDate(e.target.value);
                                    setActivePreset(null); // Clear preset when manually editing dates
                                }, style: {
                                    width: "100%",
                                    padding: "8px 12px",
                                    border: "2px solid var(--ps-gray4)",
                                    borderRadius: "var(--ps-border-radius)",
                                    fontSize: "14px",
                                    outline: "none",
                                    transition: "border-color 0.2s ease",
                                }, onFocus: function (e) {
                                    e.currentTarget.style.borderColor = "var(--ps-primary-blue)";
                                }, onBlur: function (e) {
                                    e.currentTarget.style.borderColor = "var(--ps-gray4)";
                                } })),
                        React.createElement("div", { className: "flex-1" },
                            React.createElement("label", { className: "d-block mb-5", style: {
                                    fontSize: "14px",
                                    fontWeight: "500",
                                } }, "End Date"),
                            React.createElement("input", { type: "date", value: tempEndDate, onChange: function (e) {
                                    setTempEndDate(e.target.value);
                                    setActivePreset(null); // Clear preset when manually editing dates
                                }, style: {
                                    width: "100%",
                                    padding: "8px 12px",
                                    border: "2px solid var(--ps-gray4)",
                                    borderRadius: "var(--ps-border-radius)",
                                    fontSize: "14px",
                                    outline: "none",
                                    transition: "border-color 0.2s ease",
                                }, onFocus: function (e) {
                                    e.currentTarget.style.borderColor = "var(--ps-primary-blue)";
                                }, onBlur: function (e) {
                                    e.currentTarget.style.borderColor = "var(--ps-gray4)";
                                } }))),
                    React.createElement("div", null,
                        React.createElement("button", { type: "button", onClick: function () { return setEnableShiftTimes(!enableShiftTimes); }, style: {
                                background: "none",
                                border: "none",
                                color: "var(--ps-primary-blue)",
                                cursor: "pointer",
                                fontSize: "14px",
                                fontWeight: "500",
                                textDecoration: "underline",
                                padding: "0",
                                marginBottom: "8px",
                            }, onMouseOver: function (e) {
                                e.currentTarget.style.color = "var(--primary-dark-blue, #0a4387)";
                            }, onMouseOut: function (e) {
                                e.currentTarget.style.color = "var(--primary-blue, #00aec7)";
                            } },
                            enableShiftTimes ? "Hide" : "Set",
                            " Shift Times"),
                        enableShiftTimes && (React.createElement("div", null,
                            React.createElement("div", { className: "d-flex mt-10", style: { gap: "16px" } },
                                React.createElement("div", { className: "flex-1" },
                                    React.createElement("label", { style: {
                                            marginBottom: "4px",
                                            fontSize: "12px",
                                            color: "#666",
                                        } }, "Shift Start Time *"),
                                    React.createElement("select", { value: shiftStartTime, onChange: function (e) { return setShiftStartTime(e.target.value); }, style: {
                                            width: "100%",
                                            padding: "8px 12px",
                                            border: "1px solid var(--ps-gray4)",
                                            borderRadius: "var(--ps-border-radius)",
                                            fontSize: "14px",
                                            backgroundColor: "white",
                                        } }, timeOptions.map(function (time) { return (React.createElement("option", { key: time, value: time }, time)); }))),
                                React.createElement("div", { className: "flex-1" },
                                    React.createElement("label", { style: {
                                            marginBottom: "4px",
                                            fontSize: "12px",
                                            color: "#666",
                                        } }, "Shift End Time *"),
                                    React.createElement("select", { value: shiftEndTime, onChange: function (e) { return setShiftEndTime(e.target.value); }, style: {
                                            width: "100%",
                                            padding: "8px 12px",
                                            border: "1px solid var(--ps-gray4)",
                                            borderRadius: "var(--ps-border-radius)",
                                            fontSize: "14px",
                                            backgroundColor: "white",
                                        } }, timeOptions.map(function (time) { return (React.createElement("option", { key: time, value: time }, time)); })))),
                            React.createElement("div", { className: "d-flex justify-content-end mt-10" },
                                React.createElement("button", { type: "button", onClick: function () {
                                        setShiftStartTime("No time filter");
                                        setShiftEndTime("No time filter");
                                        setEnableShiftTimes(false);
                                    }, style: {
                                        background: "none",
                                        border: "1px solid #dc3545",
                                        color: "#dc3545",
                                        cursor: "pointer",
                                        fontSize: "12px",
                                        fontWeight: "500",
                                        padding: "4px 12px",
                                        borderRadius: "var(--ps-border-radius)",
                                        transition: "all 0.2s ease",
                                    }, onMouseOver: function (e) {
                                        e.currentTarget.style.backgroundColor = "#dc3545";
                                        e.currentTarget.style.color = "white";
                                    }, onMouseOut: function (e) {
                                        e.currentTarget.style.backgroundColor = "transparent";
                                        e.currentTarget.style.color = "#dc3545";
                                    } }, "Clear Shift Times"))))),
                    showDayOfWeekFilter && (React.createElement("div", { className: "mt-15" },
                        React.createElement("button", { type: "button", onClick: function () { return setEnableDayOfWeekFilter(!enableDayOfWeekFilter); }, style: {
                                background: "none",
                                border: "none",
                                color: "var(--ps-primary-blue)",
                                cursor: "pointer",
                                fontSize: "14px",
                                fontWeight: "500",
                                textDecoration: "underline",
                                padding: "0",
                                marginBottom: "8px",
                            }, onMouseOver: function (e) {
                                e.currentTarget.style.color = "var(--primary-dark-blue, #0a4387)";
                            }, onMouseOut: function (e) {
                                e.currentTarget.style.color = "var(--primary-blue, #00aec7)";
                            } },
                            enableDayOfWeekFilter ? "Hide" : "Set",
                            " Days of Week"),
                        enableDayOfWeekFilter && (React.createElement("div", null,
                            React.createElement("div", { className: "d-flex flex-wrap mt-10", style: { gap: "8px" } }, DAY_OF_WEEK_OPTIONS.map(function (day) {
                                var isSelected = selectedDays.includes(day.value);
                                var isHovered = hoveredDay === day.value;
                                var getBackgroundColor = function () {
                                    if (isSelected)
                                        return "#00aec7";
                                    if (isHovered)
                                        return "#e9ecef";
                                    return "#f8f9fa";
                                };
                                return (React.createElement("button", { key: day.value, type: "button", onClick: function (e) {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        toggleDaySelection(day.value);
                                    }, style: {
                                        padding: "8px 16px",
                                        border: "1px solid #d0d7de",
                                        borderRadius: "4px",
                                        backgroundColor: getBackgroundColor(),
                                        color: isSelected ? "white" : "#24292f",
                                        cursor: "pointer",
                                        fontSize: "14px",
                                        fontWeight: "600",
                                        transition: "background-color 0.15s ease",
                                    }, title: day.label, onMouseEnter: function () { return setHoveredDay(day.value); }, onMouseLeave: function () { return setHoveredDay(null); } }, day.short));
                            })),
                            React.createElement("div", { className: "d-flex justify-content-end mt-10" },
                                React.createElement("button", { type: "button", onClick: function () {
                                        setSelectedDays([]);
                                        setEnableDayOfWeekFilter(false);
                                    }, style: {
                                        background: "none",
                                        border: "1px solid #dc3545",
                                        color: "#dc3545",
                                        cursor: "pointer",
                                        fontSize: "12px",
                                        fontWeight: "500",
                                        padding: "4px 12px",
                                        borderRadius: "var(--ps-border-radius)",
                                        transition: "all 0.2s ease",
                                    }, onMouseOver: function (e) {
                                        e.currentTarget.style.backgroundColor = "#dc3545";
                                        e.currentTarget.style.color = "white";
                                    }, onMouseOut: function (e) {
                                        e.currentTarget.style.backgroundColor = "transparent";
                                        e.currentTarget.style.color = "#dc3545";
                                    } }, "Clear Days"))))))),
                React.createElement("div", { className: "d-flex justify-content-end mt-10" },
                    React.createElement("button", { onClick: handleApply, style: {
                            padding: "6px 16px",
                            border: "1px solid #00aec7",
                            borderRadius: "var(--ps-border-radius)",
                            backgroundColor: "#00aec7",
                            color: "white",
                            cursor: "pointer",
                            fontSize: "14px",
                            fontWeight: "500",
                            transition: "background-color 0.2s ease",
                        }, onMouseEnter: function (e) {
                            e.currentTarget.style.backgroundColor = "#0a4387";
                        }, onMouseLeave: function (e) {
                            e.currentTarget.style.backgroundColor = "#00aec7";
                        } }, "Apply")))))));
});
DateRangePicker.displayName = "DateRangePicker";
// Helper function for preset labels
var getPresetLabel = function (preset) {
    var currentYear = new Date().getFullYear();
    var lastYear = currentYear - 1;
    var labels = {
        "1d": "Last 24 hours",
        "7d": "Last 7 days",
        "14d": "Last 14 days",
        "30d": "Last 30 days",
        "90d": "Last 90 days",
        ytd: "Year to date (".concat(currentYear, ")"),
        lastyear: "Full year ".concat(lastYear),
    };
    return labels[preset] || preset;
};
export default DateRangePicker;
